import {
  ChatBotAction,
  ChatBotDeepLinkAction,
  IAction,
} from '@varo/graphql-schema-types';
import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

interface Props {
  option: IAction;
  index: number;
  isDisabled: boolean;
  onPress: (option: ChatBotAction | ChatBotDeepLinkAction) => void;
}

const AnimatedBotOption = ({ option, index, isDisabled, onPress }: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(20);

  useEffect(() => {
    opacity.value = withDelay(index * 100, withTiming(1, { duration: 300 }));
    translateY.value = withDelay(index * 100, withTiming(0, { duration: 300 }));
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    };
  });

  return (
    <Animated.View
      style={[styles.faqButton, isDisabled ? null : animatedStyle]}
    >
      <Pressable
        style={[
          styles.button,
          isFocused && styles.focus,
          isHovered && styles.hover,
          isPressed && styles.press,
          isDisabled && styles.isDisabled,
        ]}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        onPress={() => onPress(option as ChatBotAction | ChatBotDeepLinkAction)}
        aria-label={option.copy}
        role="button"
        testID={option.copy}
        disabled={isDisabled}
      >
        <BodyBaseText
          style={[styles.copy, isDisabled && styles.isDisabledText]}
        >
          {option.copy}
        </BodyBaseText>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: theme.color.interactive.primary,
    borderRadius: 4,
    borderWidth: 1,
    display: 'flex',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    width: '100%',
  },
  copy: {
    color: theme.color.interactive.primary,
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    height: 'auto',
    letterSpacing: 0.28,
    lineHeight: 24,
  },
  faqButton: {
    alignSelf: 'flex-start',
    paddingVertical: theme.spacing.xs,
  },
  focus: {
    borderColor: theme.color.border.focused,
  },
  hover: {
    borderColor: theme.color.interactive.primaryHovered,
  },
  isDisabled: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
  },
  isDisabledText: {
    color: theme.color.text.tertiary,
  },
  press: {
    borderColor: theme.color.interactive.primaryPressed,
  },
});

export default AnimatedBotOption;
